import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import LogoMark from "@/components/LogoMark";

export default function HeaderNav({ t, lang, setLang, onNavigate }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollYProgress } = useScroll();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { id: "#portfolio", label: t.nav.work },
        { id: "#sluzby", label: t.nav.services },
        { id: "#proces", label: t.nav.process },
        { id: "#kontakt", label: t.nav.contact },
    ];

    const go = (id) => {
        setOpen(false);
        onNavigate(id);
    };

    return (
        <header
            data-testid="navbar-container"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled
                    ? "bg-ink/90 backdrop-blur-md border-b border-white/5"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-6xl items-center justify-between px-5 sm:px-8 py-4">
                <button
                    data-testid="navbar-logo-link"
                    onClick={() => go("#hero")}
                    className="flex items-center gap-3"
                    aria-label="Webryno Studio"
                >
                    <LogoMark size={28} />
                    <span className="font-display text-lg font-medium tracking-wide whitespace-nowrap">
                        Webryno <span className="text-glacier">Studio</span>
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id.slice(1)}`}
                            onClick={() => go(l.id)}
                            className="font-mono text-[11px] uppercase tracking-[0.22em] text-fog transition-colors duration-300 hover:text-ivory"
                        >
                            {l.label}
                        </button>
                    ))}
                    <a
                        href="/blog"
                        data-testid="nav-link-blog"
                        className="font-mono text-[11px] uppercase tracking-[0.22em] text-fog transition-colors duration-300 hover:text-ivory"
                    >
                        {t.nav.blog}
                    </a>
                </nav>

                <div className="flex items-center gap-4">
                    <div data-testid="language-switch-button" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest">
                        {["sk", "en"].map((l, i) => (
                            <span key={l} className="flex items-center gap-1.5">
                                {i === 1 && <span className="text-white/20">/</span>}
                                <button
                                    data-testid={`lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`transition-colors duration-300 ${
                                        lang === l ? "text-glacier" : "text-fog hover:text-ivory"
                                    }`}
                                >
                                    {l}
                                </button>
                            </span>
                        ))}
                    </div>
                    <button
                        data-testid="navbar-contact-cta"
                        onClick={() => go("#kontakt")}
                        className="hidden sm:inline-flex items-center rounded-sm bg-glacier btn-shine px-5 py-2.5 font-display text-xs font-semibold uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-ivory hover:shadow-[0_0_28px_rgba(74,222,128,0.3)]"
                    >
                        {t.nav.cta}
                    </button>
                    <a
                        data-testid="nav-admin-link"
                        href="/admin"
                        aria-label="Admin"
                        title="Admin"
                        className="hidden sm:inline-flex items-center justify-center text-fog transition-colors duration-300 hover:text-glacier"
                    >
                        <Lock size={15} />
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-ivory p-1"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <motion.div
                data-testid="scroll-progress-bar"
                style={{ scaleX: scrollYProgress }}
                className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-glacier"
            />
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden border-t border-white/5 bg-ink/95 backdrop-blur-md"
                    >
                        <div className="flex flex-col px-6 py-4 gap-1">
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id.slice(1)}`}
                                    onClick={() => go(l.id)}
                                    className="text-left py-3 font-display text-lg text-ivory border-b border-white/5"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                href="/blog"
                                data-testid="mobile-nav-link-blog"
                                onClick={() => setOpen(false)}
                                className="text-left py-3 font-display text-lg text-ivory border-b border-white/5"
                            >
                                {t.nav.blog}
                            </a>
                            <button
                                data-testid="mobile-nav-cta"
                                onClick={() => go("#kontakt")}
                                className="mt-3 mb-2 rounded-sm bg-glacier btn-shine px-5 py-3 font-display text-sm font-semibold uppercase tracking-widest text-ink"
                            >
                                {t.nav.cta}
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
